// src/utils/authUtils.ts

// ===== SSR/ビルド安全ガード =====
const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';

// ===== PWA（standalone）誤検知回避 =====
export const isStandalonePWA = (): boolean => {
  const isIOSStandalone = hasNavigator && (navigator as any).standalone === true;
  const isDisplayStandalone =
    hasWindow &&
    'matchMedia' in window &&
    window.matchMedia('(display-mode: standalone)').matches;
  return isIOSStandalone || isDisplayStandalone;
};

// ===== UAユーティリティ =====
const getUA = () => (hasNavigator ? navigator.userAgent || '' : '');
const getUAL = () => getUA().toLowerCase();

// iOSの実ブラウザ判定マーク（WebKitでも"通常ブラウザ"として扱う）
const IOS_REAL_BROWSER_MARKS = ['CriOS', 'FxiOS', 'EdgiOS', 'OPiOS', 'DuckDuckGo'];

// iOSの代表的な In-App Browser シグナル（検出時は通常ブラウザ扱いにしない）
const IOS_INAPP_MARKS = [
  'FBAN',
  'FBAV',
  'FBIOS',
  'Instagram',
  'Line',
  'GSA',          // Google App
  'GMail',        // Gmail アプリ内ブラウザ
  'Twitter',
  'MicroMessenger', // WeChat
];

// Androidの代表的な In-App Browser シグナル
const ANDROID_INAPP_MARKS = [
  'fb_iab', 'fbav', 'fban',
  'instagram',
  'line/',
  'twitter',
  'micromessenger', // WeChat
  'gsa',            // Google App
];

// ===== 通常ブラウザかどうか（最終的な肯否） =====
export const isRealBrowser = (): boolean => {
  if (!hasNavigator) return true;        // SSR中は真扱い
  if (isStandalonePWA()) return true;    // PWAは通常扱い

  const ua = getUA();
  const uaL = getUAL();

  const isIOS = /iphone|ipad|ipod/.test(uaL);
  const isAndroid = /android/.test(uaL);

  if (isIOS) {
    // iOS: まず代表的なIn-App/埋め込みブラウザのシグナルを弾く（Safari/Versionを含んでいても除外）
    const hasIOSInAppMark = IOS_INAPP_MARKS.some(mark => ua.includes(mark) || uaL.includes(mark.toLowerCase()));
    if (hasIOSInAppMark) return false;

    // iOS: Chrome/Firefox/Edge/Opera/DDG（WebKitベースだが通常ブラウザとして扱う）
    if (IOS_REAL_BROWSER_MARKS.some(mark => ua.includes(mark))) return true;
    // iOS Safari（通常 "Safari" と "Version/" を含む）
    const hasSafari = /safari/i.test(ua);
    const hasVersion = /version\//i.test(ua);
    if (hasSafari && hasVersion) return true;
    // それ以外はWebView/In-Appの可能性が高い
    return false;
  }

  if (isAndroid) {
    // Android WebViewは "; wv" が強いシグナル
    if (ua.includes('; wv')) return false;
    // 代表的In-Appなら"実ブラウザ"ではない
    if (ANDROID_INAPP_MARKS.some(mark => uaL.includes(mark))) return false;
    // それ以外はChrome/Firefox/Edge/Samsung Internet等の通常ブラウザとみなす
    return true;
  }

  // PC等はOK
  return true;
};

// ===== In-App / WebView 判定（逆概念） =====
export const isInAppBrowserOrWebView = (): boolean => {
  if (!hasNavigator) return false;
  if (isStandalonePWA()) return false;
  return !isRealBrowser();
};

// ===== 外部ブラウザ誘導が必要か =====
export const shouldUseExternalBrowser = (): boolean => {
  return isInAppBrowserOrWebView();
};

// ===== テスト検証用フラグ（検証完了後は false に変更） =====
const DEBUG_AUTH = false; // 🔧 検証完了後は false に変更

// ===== ロギング（運用・デバッグ用） =====
export const logAuthAttempt = (
  context: string,
  success: boolean,
  error?: unknown
) => {
  try {
    const ua = getUA();
    const payload = {
      context,
      success,
      error_code: (error as any)?.code,
      error_message: (error as any)?.message,
      url: hasWindow ? window.location.href : undefined,
      ua,
      flags: {
        standalonePWA: isStandalonePWA(),
        realBrowser: isRealBrowser(),
        inAppOrWV: isInAppBrowserOrWebView(),
      },
      timestamp: new Date().toISOString(),
    };

    // テスト検証用の詳細ログ
    if (DEBUG_AUTH) {
      // eslint-disable-next-line no-console
      console.group(`🔍 [DEBUG] Auth Attempt: ${context}`);
      if (error) {}
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  } catch {
    // ログ失敗は無視
  }
};

// ===== エラーハンドリング（共通） =====
export const handleAuthError = (error: any) => {
  const code: string | undefined = error?.code;
  // よくあるPopup系エラー → Redirectでフォールバックする判断材料に
  const popupFailures = new Set([
    'auth/popup-blocked',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/internal-error',
  ]);

  return {
    code,
    isPopupFailure: code ? popupFailures.has(code) : true,
    isDisallowedUA: code === 'auth/operation-not-allowed' || // 稀
                     String(error?.message || '').toLowerCase().includes('disallowed_useragent'),
  };
};

// ===== プラットフォーム判定 =====
export const isIOS = (): boolean => {
  const uaL = getUAL();
  return /iphone|ipad|ipod/.test(uaL);
};
